import { useEffect, useMemo, useRef, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useCliente, useClienti } from '@/hooks/useClienti';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { usePermissions } from '@/hooks/usePermissions';
import { parseStaffClienteId } from '@/hooks/useTenantScope';
import { paths } from '@/routes/paths';
import { useUIStore } from '@/stores/useUIStore';

interface Option {
  id: string | null;
  label: string;
}

const ALL_OPTION: Option = { id: null, label: 'Tutti i clienti' };

function clienteDetailId(pathname: string): string | null {
  const prefix = `${paths.admin.clienti}/`;
  if (!pathname.startsWith(prefix)) return null;
  const rest = pathname.slice(prefix.length);
  if (!rest || rest.includes('/')) return null;
  return rest;
}

/**
 * Tenant selector for internal staff.
 *
 * This is a *view* filter, not an authorisation bypass: staff already see every
 * tenant through RLS (`tenant_visible` returns true for operators), and the
 * selection is stored in the UI store so every page can scope lists, KPIs and
 * create-forms to one customer. Tenant users never see this control.
 *
 * Mount only one instance. The desktop sidebar and the mobile drawer are
 * mutually exclusive; a second mount races the URL sync.
 */
export function TenantSwitcher(): JSX.Element | null {
  const { canSwitchTenant } = usePermissions();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = useUIStore((state) => state.impersonatedClienteId);
  const storedLabel = useUIStore((state) => state.impersonatedClienteLabel);
  const setSelected = useUIStore((state) => state.setImpersonatedClienteId);
  const pendingUrlId = useRef<string | null | undefined>(undefined);

  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const search = useDebouncedValue(input, 300);

  const { data, isLoading } = useClienti({
    search: search || undefined,
    pageSize: 20,
    sortBy: 'ragione_sociale',
    sortDir: 'asc',
  });
  const selectedQuery = useCliente(selectedId ?? undefined);
  const selectedDetail =
    selectedQuery.data?.id === selectedId ? selectedQuery.data : undefined;

  useEffect(() => {
    if (selectedId && selectedQuery.isError && !selectedQuery.isPlaceholderData) {
      setSelected(null);
    }
  }, [selectedId, selectedQuery.isError, selectedQuery.isPlaceholderData, setSelected]);

  useEffect(() => {
    if (!canSwitchTenant) return;
    const raw = searchParams.get('clienteId');
    if (raw === 'unassigned') return;
    const fromUrl = parseStaffClienteId(raw) ?? null;

    if (pendingUrlId.current !== undefined) {
      if (fromUrl === pendingUrlId.current) pendingUrlId.current = undefined;
      return;
    }

    if (fromUrl === selectedId) return;
    if (fromUrl) {
      setSelected(fromUrl);
      return;
    }
    if (!selectedId) return;

    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (next.get('clienteId') === selectedId) return previous;
        next.set('clienteId', selectedId);
        return next;
      },
      { replace: true },
    );
  }, [canSwitchTenant, searchParams, selectedId, setSearchParams, setSelected]);

  const selectedOption = useMemo<Option>(() => {
    if (!selectedId) return ALL_OPTION;
    const fromList = (data?.data ?? []).find((cliente) => cliente.id === selectedId);
    return {
      id: selectedId,
      label: selectedDetail?.ragione_sociale ?? fromList?.ragione_sociale ?? storedLabel ?? 'Cliente selezionato',
    };
  }, [selectedId, selectedDetail, data, storedLabel]);

  const options = useMemo<Option[]>(() => {
    const clients = (data?.data ?? []).map((cliente) => ({ id: cliente.id, label: cliente.ragione_sociale }));
    const withSelected =
      selectedOption.id && !clients.some((cliente) => cliente.id === selectedOption.id)
        ? [selectedOption, ...clients]
        : clients;
    return [ALL_OPTION, ...withSelected];
  }, [data, selectedOption]);

  const applySelection = (nextId: string | null, nextLabel: string | null): void => {
    pendingUrlId.current = nextId;
    setSelected(nextId, nextLabel);
    setInput('');
    setOpen(false);

    const detailId = clienteDetailId(pathname);
    if (detailId) {
      if (!nextId) {
        pendingUrlId.current = undefined;
        void navigate(paths.admin.clienti);
        return;
      }
      if (nextId !== detailId) {
        const next = new URLSearchParams(searchParams);
        next.set('clienteId', nextId);
        const query = next.toString();
        void navigate(`${paths.admin.cliente(nextId)}${query ? `?${query}` : ''}`);
        return;
      }
    }

    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (nextId) {
          if (next.get('clienteId') === nextId) {
            pendingUrlId.current = undefined;
            return previous;
          }
          next.set('clienteId', nextId);
        } else if (next.get('clienteId') === 'unassigned') {
          pendingUrlId.current = undefined;
          return previous;
        } else {
          if (!next.has('clienteId')) {
            pendingUrlId.current = undefined;
            return previous;
          }
          next.delete('clienteId');
        }
        return next;
      },
      { replace: true },
    );
  };

  if (!canSwitchTenant) return null;

  return (
    <Autocomplete
      options={options}
      value={selectedOption}
      open={open}
      onOpen={() => {
        setOpen(true);
        setInput('');
      }}
      onClose={() => {
        setOpen(false);
        setInput('');
      }}
      inputValue={open ? input : selectedOption.label}
      filterOptions={(opts) => opts}
      loading={isLoading}
      onChange={(_event, option) => {
        const nextId = option?.id ?? null;
        applySelection(nextId, nextId ? option?.label ?? null : null);
      }}
      onInputChange={(_event, next, reason) => {
        if (reason === 'input') setInput(next);
        if (reason === 'clear') setInput('');
      }}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      disableClearable
      size="small"
      sx={{ width: '100%', minWidth: 0 }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Cliente"
          placeholder="Cerca cliente…"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <Box sx={{ display: 'flex', alignItems: 'center', pl: 1, color: 'text.secondary' }}>
                <BusinessOutlinedIcon fontSize="small" />
              </Box>
            ),
          }}
        />
      )}
    />
  );
}
